import React, { useState, useEffect } from 'react';
import { 
  getTaskCategories, 
  saveTaskCategories, 
  addTaskCategory, 
  removeTaskCategory,
  updateCategoryDueDays,
  formatCategoryForDisplay 
} from '../lib/taskCategories';
import { 
  PlusIcon, 
  TrashIcon, 
  TagIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const TaskCategorySettings = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCategory, setNewCategory] = useState('');
  const [newCategoryDueDays, setNewCategoryDueDays] = useState(5);
  const [saving, setSaving] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [editingDueDays, setEditingDueDays] = useState({});

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const categoriesData = await getTaskCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;

    try {
      setSaving(true);
      const updatedCategories = await addTaskCategory(newCategory, newCategoryDueDays);
      setCategories(updatedCategories);
      setNewCategory('');
      setNewCategoryDueDays(5);
    } catch (error) {
      alert(error.message || 'Failed to add category');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = (category) => {
    setDeletingCategory(category);
  };

  const confirmDeleteCategory = async () => {
    if (!deletingCategory) return;

    try {
      setSaving(true);
      const categoryName = typeof deletingCategory === 'string' ? deletingCategory : deletingCategory.name;
      const updatedCategories = await removeTaskCategory(categoryName);
      setCategories(updatedCategories);
      setDeletingCategory(null);
    } catch (error) {
      alert('Failed to delete category');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateDueDays = async (categoryName, dueDays) => {
    try {
      setSaving(true);
      const updatedCategories = await updateCategoryDueDays(categoryName, dueDays);
      setCategories(updatedCategories);
      setEditingDueDays({});
    } catch (error) {
      alert('Failed to update due days');
    } finally {
      setSaving(false);
    }
  };

  const handleReorderCategories = async (newOrder) => {
    try {
      setSaving(true);
      await saveTaskCategories(newOrder);
      setCategories(newOrder);
    } catch (error) {
      alert('Failed to save category order');
    } finally {
      setSaving(false);
    }
  };

  const moveCategory = (index, direction) => {
    const newCategories = [...categories];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newCategories.length) return;
    
    [newCategories[index], newCategories[targetIndex]] = [newCategories[targetIndex], newCategories[index]];
    handleReorderCategories(newCategories);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
        <span className="ml-2 text-gray-600">Loading task categories...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
      <div className="flex items-center mb-6">
        <TagIcon className="w-6 h-6 text-pink-600 mr-3" />
        <h2 className="text-xl font-bold text-gray-900">Task Categories</h2>
      </div>
      
      <p className="text-gray-600 mb-6">
        Manage the categories available when creating tasks. These categories help organize and filter tasks by type. The "Quote due in" setting determines how many days suppliers have to submit quotes for tasks in each category.
      </p>

      {/* Add New Category */}
      <form onSubmit={handleAddCategory} className="mb-6">
        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Enter new category name..."
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            disabled={saving}
          />
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 whitespace-nowrap">Quote due in:</label>
            <input
              type="number"
              min="1"
              max="30"
              value={newCategoryDueDays}
              onChange={(e) => setNewCategoryDueDays(parseInt(e.target.value) || 5)}
              className="w-16 px-2 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              disabled={saving}
            />
            <span className="text-sm text-gray-600">days</span>
          </div>
          <button
            type="submit"
            disabled={saving || !newCategory.trim()}
            className="px-6 py-3 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Category
          </button>
        </div>
      </form>

      {/* Categories List */}
      <div className="space-y-2 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-700">Current Categories</h3>
          <span className="text-sm text-gray-500">{categories.length} categories</span>
        </div>
        
        {categories.map((category, index) => {
          const categoryName = typeof category === 'string' ? category : category.name;
          const dueDays = typeof category === 'string' ? 5 : category.dueDays;
          const isEditingDays = editingDueDays[categoryName];
          
          return (
            <div 
              key={categoryName} 
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex items-center flex-1">
                <span className="font-medium text-gray-900">
                  {formatCategoryForDisplay(categoryName)}
                </span>
                <span className="ml-2 px-2 py-1 text-xs bg-pink-100 text-pink-600 rounded">
                  {categoryName}
                </span>
                <div className="ml-4 flex items-center gap-2">
                  {isEditingDays ? (
                    <>
                      <input
                        type="number"
                        min="1"
                        max="30"
                        value={isEditingDays}
                        onChange={(e) => setEditingDueDays({...editingDueDays, [categoryName]: parseInt(e.target.value) || 5})}
                        className="w-16 px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-600">days</span>
                      <button
                        onClick={() => handleUpdateDueDays(categoryName, editingDueDays[categoryName])}
                        className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingDueDays({})}
                        className="text-xs px-2 py-1 bg-gray-400 text-white rounded hover:bg-gray-500"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-gray-600">Quote due in: {dueDays} days</span>
                      <button
                        onClick={() => setEditingDueDays({[categoryName]: dueDays})}
                        className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Edit
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => moveCategory(index, 'up')}
                  disabled={index === 0 || saving}
                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  onClick={() => moveCategory(index, 'down')}
                  disabled={index === categories.length - 1 || saving}
                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Move down"
                >
                  ↓
                </button>
                <button
                  onClick={() => handleDeleteCategory(category)}
                  disabled={saving}
                  className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                  title="Delete category"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}

        {categories.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            No categories configured. Add your first category above.
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deletingCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
              </div>
              
              <h2 className="text-xl font-bold text-gray-900 mb-4">Delete Category</h2>
              
              <p className="text-gray-700 mb-6">
                Are you sure you want to delete the category <strong>"{formatCategoryForDisplay(typeof deletingCategory === 'string' ? deletingCategory : deletingCategory?.name)}"</strong>?
              </p>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="text-yellow-800 text-sm text-left">
                    <p className="font-semibold">Warning</p>
                    <p>Existing tasks with this category will keep their current category, but you won't be able to assign this category to new tasks.</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setDeletingCategory(null)}
                  className="flex-1 px-4 py-3 bg-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteCategory}
                  className="flex-1 px-4 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">How Task Categories Work:</h3>
        <div className="text-blue-800 text-sm space-y-1">
          <p>• Categories appear in the dropdown when creating or editing tasks</p>
          <p>• Use categories to organize tasks by work type (upholstery, delivery, etc.)</p>
          <p>• Each category has a default quote due date duration in days</p>
          <p>• Categories help filter and group tasks in project views</p>
          <p>• You can reorder categories using the ↑ ↓ buttons</p>
        </div>
      </div>
    </div>
  );
};

export default TaskCategorySettings;